import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Play,
    Wallet,
    Crown,
    Heart,
    UserCog,
    PlusCircle,
    LayoutDashboard,
    Calendar,
    Briefcase,
    Clock,
    CheckCircle,
    GraduationCap,
    BellRing,
    Menu,
    X,
    Star,
    MapPin,
    CalendarDays,
    Building2,
} from "lucide-react";
import "./DashboardCompany.scss";

import CompanyAgenda from "./Components/CompanyAgenda/CompanyAgenda";
import OpenVacancies from "./Components/OpenVacancies/OpenVacancies";
import InProgressJobs from "../../Components/SharedInProgress/InProgress";
import CompletedJobs from "../DashboardCompany/Components/CompletedJobs/CompletedJobs";
import Training from "./Components/Training";
import Favorites from "../DashboardCompany/Components/Favorites/Favorites";

import { api } from "../../config/api";
import { getMe, updateProfile, corporateContracts, markTutorialSeen } from "../../services/authService";
import { createJob } from "../../services/jobService";
import { getWallet } from "../../services/walletService";
import ProfileModal from "../../Components/FreelancerProfileModal/FreelancerProfileModal";
import CreateJobModal from '../../Components/CreateJobModal/CreateJobModal';
import NotificationBell from '../../Components/Notificationbell/Notificationbell';
import ChatSupportButton from "../../Components/ChatSupportButton/ChatSupportButton";
import WalletModal from "../../Components/WalletModal/WalletModal";
import GoldSubscriptionCompany from "../../Components/GoldSubscriptionCompany/GoldSubscriptionCompany";
import CompanyGoldTrialModal from "../../Components/CompanyGoldTrialModal/CompanyGoldTrialModal"; // <-- Importe o modal criado aqui (ajuste o caminho se necessário)
import Toast from "../../Components/Toast/Toast";
import TutorialCompany from "../../Components/TutorialCompany/TutorialCompany";
import InstallPwaBanner from "../../Components/InstallPwaBanner/InstallPwaBanner";
import NotificationSettings from "../../Components/NotificationSettings/NotificationSettings";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import { getInitialTab } from "../../utils/dashboardTab";

const COMPANY_TABS = ["agenda", "vagas", "andamento", "finalizadas", "treinamento", "notificacoes"];

const DashboardCompany = () => {
    const navigate = useNavigate();
    // ?tab=... vem dos links das notificações (push e e-mail)
    const [activeTab, setActiveTab] = useState(() => getInitialTab(COMPANY_TABS, "agenda"));

    // Reinscreve este aparelho no push (uma vez por sessão) se já tiver permissão
    usePushNotifications();
    const [user, setUser] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showCreateJobModal, setShowCreateJobModal] = useState(false);
    const [showFavoritesModal, setShowFavoritesModal] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showWalletModal, setShowWalletModal] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [showGoldTrialModal, setShowGoldTrialModal] = useState(false); // <-- Novo estado para o modal de trial
    const [wallet, setWallet] = useState(null);
    const [contractStatus, setContractStatus] = useState(null);
    const [goldActive, setGoldActive] = useState(null);
    const isFreePlan = goldActive === false;

    const [toast, setToast] = useState({ message: "", type: "success" });
    const [showTutorial, setShowTutorial] = useState(false);

    // ─── NOVO: endereço da empresa vem do próprio `user` (mesmo padrão do FreelancerProfileModal) ──
    const companyAddress = user?.address || user?.addressData || null;

    const showJobToast = ({ title, description, variant }) => {
        setToast({
            message: description || title || "",
            type: variant === "destructive" ? "error" : "success",
        });
    };

    useEffect(() => {
        const hasSeen = localStorage.getItem('tutorialCompany');
        if (!hasSeen) {
            const timer = setTimeout(() => {
                setShowTutorial(true);
            }, 800);
            return () => clearTimeout(timer);
        }
    }, []);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(Number(value) || 0);
    };

    useEffect(() => {
        loadWallet();
        loadContractStatus();
        loadGoldSubscription();
    }, []);

    async function loadGoldSubscription() {
        try {
            const { data } = await api.get("/subscriptions/me");
            setGoldActive(!!data?.hasActivePlan);
        } catch (err) {
            setGoldActive(false);
        }
    }

    async function loadContractStatus() {
        try {
            const data = await corporateContracts.getMine();
            setContractStatus(data?.status ?? null);
        } catch (err) {
            setContractStatus(null);
        }
    }

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) {
                setIsSidebarOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const loadUser = async () => {
            try {
                const data = await getMe();
                setUser(data);
            } catch (error) {
                console.error(error);
            }
        };

        loadUser();
    }, []);

    async function loadWallet() {
        try {
            const data = await getWallet();
            setWallet(data);
        } catch (err) {
            console.error(err);
        }
    }

    const tabs = [
        { id: "agenda", label: "Agenda", icon: Calendar },
        { id: "vagas", label: "Vagas Abertas", icon: Briefcase },
        { id: "andamento", label: "Em Andamento", icon: Clock },
        { id: "finalizadas", label: "Finalizadas", icon: CheckCircle },
        { id: "notificacoes", label: "Notificações", icon: BellRing },
    ];

    const mobileMenuItems = [
        { id: "agenda", label: "Agenda", icon: Calendar },
        { id: "vagas", label: "Vagas Abertas", icon: Briefcase },
        { id: "andamento", label: "Em Andamento", icon: Clock },
        { id: "finalizadas", label: "Finalizadas", icon: CheckCircle },
        { id: "treinamento", label: "Treinamento", icon: GraduationCap },
        { id: "notificacoes", label: "Notificações", icon: BellRing },
    ];

    const renderContent = () => {
        switch (activeTab) {
            case "agenda":
                return (
                    <CompanyAgenda
                        isFreePlan={isFreePlan}
                        companyAddress={companyAddress}
                        onSaveCompanyAddress={handleSaveCompanyAddress}
                        walletBalance={wallet ? (wallet.balance ?? 0) - (wallet.reserved ?? 0) : null} 
                        onOpenWallet={() => setShowWalletModal(true)}                                    
                    />
                );
            case "vagas":
                return <OpenVacancies />;
            case "andamento":
                return <InProgressJobs />;
            case "finalizadas":
                return <CompletedJobs />;
            case "treinamento":
                return <Training />;
            case "notificacoes":
                return <NotificationSettings userType="company" />;
            default:
                return (
                    <CompanyAgenda
                        isFreePlan={isFreePlan}
                        companyAddress={companyAddress}
                        onSaveCompanyAddress={handleSaveCompanyAddress}
                    />
                );
        }
    };

    const handleUpdateProfile = async (formData) => {
        try {
            const updatedUser = await updateProfile(user.id, formData);
            setUser(updatedUser);
            localStorage.setItem("user", JSON.stringify(updatedUser));
            return { success: true, message: "Perfil atualizado com sucesso!" };
        } catch (error) {
            return { success: false, message: error?.response?.data || "Erro ao atualizar perfil" };
        }
    };

    // ─── NOVO: salva só o endereço da empresa, chamado pelo CreateJobModal ──
    const handleSaveCompanyAddress = async (addressData) => {
        try {
            const updatedUser = await updateProfile(user.id, { address: addressData });
            setUser(updatedUser);
            localStorage.setItem("user", JSON.stringify(updatedUser));
            return { success: true, address: updatedUser?.address ?? addressData };
        } catch (error) {
            return { success: false, message: error?.response?.data || "Erro ao salvar endereço" };
        }
    };

    const handleCreateJob = async (jobData) => {
        try {
            const newJob = await createJob(user.id, jobData);
            return newJob;
        } catch (error) {
            console.error('Erro ao criar vaga:', error);
            throw error;
        }
    };

    const handlePremiumSubscribe = (data) => {
        alert('🎉 Plano Premium ativado com sucesso!');
    };

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        if (isMobile) {
            setIsSidebarOpen(false);
        }
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    return (
        <div className="dashboard-company">
            {/* ========== VERSÃO DESKTOP ========== */}
            <div className="dashboard-company__desktop">
                <header className="dashboard-company__header">
                    <div className="dashboard-company__header-left">
                        <div className="dashboard-company__profile">
                            <div className="dashboard-company__avatar-wrapper">
                                <img
                                    src={user?.avatarUrl || "https://ui-avatars.com/api/?name=Empresa&background=1ec88a&color=fff&size=128"}
                                    alt="Perfil"
                                    className="dashboard-company__avatar"
                                />
                                <span className="dashboard-company__status-indicator" />
                            </div>

                            <div className="dashboard-company__user-info">
                                <h1 className="dashboard-company__greeting">
                                    {user?.name ?? "Carregando..."}
                                </h1>
                                <div className="dashboard-company__user-meta">
                                    <button
                                        type="button"
                                        className="dashboard-header__role"
                                        onClick={() => navigate("/painel-profissional")}
                                    >
                                        <Building2 size={14} />
                                        Painel Corporativo
                                    </button>

                                    {(contractStatus === 'Active' || goldActive) && (
                                        <span className="dashboard-company__plan-badge">
                                            <Crown size={12} />
                                            GOLD
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-company__header-actions">
                        <div
                            className="dashboard-sidebar__info-item dashboard-sidebar__info-item--wallet"
                            onClick={() => setShowWalletModal(true)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                                e.key === 'Enter' && setShowWalletModal(true)
                            }
                        >
                            <div className="dashboard-sidebar__wallet-icon">
                                <Wallet size={16} strokeWidth={2} />
                            </div>

                            <div className="dashboard-sidebar__wallet-content">
                                <span className="dashboard-sidebar__wallet-label">
                                    Saldo disponível
                                </span>

                                <strong className="dashboard-sidebar__wallet-value">
                                    {formatCurrency(
                                        (wallet?.balance ?? 0) - (wallet?.reserved ?? 0)
                                    )}
                                </strong>
                            </div>
                        </div>

                        <button
                            className="dashboard-company__action-btn dashboard-company__action-btn--premium"
                            onClick={() => setShowPremiumModal(true)}
                        >
                            <Crown size={18} />
                            <span className="dashboard-company__action-label">Premium</span>
                        </button>

                        <button
                            className="dashboard-company__action-btn dashboard-company__action-btn--favorites"
                            onClick={() => setShowFavoritesModal(true)}
                        >
                            <Heart size={18} />
                            <span className="dashboard-company__action-label">Favoritos</span>
                        </button>

                        <button
                            className="dashboard-company__action-btn dashboard-company__action-btn--profile"
                            onClick={() => setShowProfileModal(true)}
                        >
                            <UserCog size={18} />
                            <span className="dashboard-company__action-label">Configurações</span>
                        </button>

                        <NotificationBell />

                        <button
                            className="dashboard-company__action-btn dashboard-company__action-btn--new-job"
                            onClick={() => setShowCreateJobModal(true)}
                        >
                            <PlusCircle size={18} />
                            <span className="dashboard-company__action-label">Nova Vaga</span>
                        </button>
                    </div>
                </header>

                <nav className="dashboard-company__nav">
                    <div className="dashboard-company__nav-tabs">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    className={`dashboard-company__nav-tab ${isActive ? 'dashboard-company__nav-tab--active' : ''}`}
                                    onClick={() => handleTabClick(tab.id)}
                                >
                                    <Icon size={18} />
                                    <span className="dashboard-company__nav-label">{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    <button
                        className={`dashboard-company__training-btn ${activeTab === "treinamento" ? 'dashboard-company__training-btn--active' : ''}`}
                        onClick={() => handleTabClick("treinamento")}
                    >
                        <Play size={16} />
                        <span>Treinamento</span>
                        <span className="dashboard-company__training-icon">✨</span>
                    </button>
                </nav>
            </div>

            {/* ========== VERSÃO MOBILE ========== */}
            <div className="dashboard-company__mobile">
                <header className="dashboard-company__mobile-header">
                    <button
                        className="dashboard-company__menu-btn"
                        onClick={toggleSidebar}
                        aria-label="Abrir menu"
                    >
                        <Menu size={24} />
                        {!isSidebarOpen && (
                            <span className="dashboard-company__glow" />
                        )}
                    </button>

                    <div className="dashboard-company__mobile-user">
                        <img
                            src={user?.avatarUrl || "https://ui-avatars.com/api/?name=Empresa&background=1ec88a&color=fff&size=128"}
                            alt="Perfil"
                            className="dashboard-company__mobile-avatar"
                        />
                        <div>
                            <span className="dashboard-company__mobile-name">{user?.name || "Empresa"}</span>
                            <button
                                type="button"
                                className="dashboard-header__role"
                                onClick={() => navigate("/painel-profissional")}
                            >
                                <Building2 size={14} />
                                Painel Corporativo
                            </button>
                            {(contractStatus === 'Active' || goldActive) && (
                                <span className="dashboard-company__plan-badge">
                                    <Crown size={12} />
                                    GOLD
                                </span>
                            )}
                        </div>
                    </div>

                    <NotificationBell />
                </header>
                <InstallPwaBanner />
                <aside className={`dashboard-company__sidebar ${isSidebarOpen ? 'dashboard-company__sidebar--open' : ''}`}>
                    <div className="dashboard-company__sidebar-header">
                        <div className="dashboard-company__sidebar-profile">
                            <div className="dashboard-company__sidebar-avatar-wrapper">
                                <img
                                    src={user?.avatarUrl || "https://ui-avatars.com/api/?name=Empresa&background=1ec88a&color=fff&size=128"}
                                    alt="Perfil"
                                    className="dashboard-company__sidebar-avatar"
                                />
                                <span className="dashboard-company__sidebar-status" />
                            </div>

                            <div className="dashboard-company__sidebar-user-info">
                                <h2 className="dashboard-company__sidebar-name">
                                    {user?.name || "Empresa"}
                                </h2>
                                <button
                                    type="button"
                                    className="dashboard-header__role"
                                    onClick={() => navigate("/painel-profissional")}
                                >
                                    <Building2 size={14} />
                                    Painel Corporativo
                                </button>

                                {contractStatus === 'Active' && (
                                    <span className="dashboard-company__plan-badge" style={{ marginTop: '4px' }}>
                                        <Crown size={12} />
                                        GOLD
                                    </span>
                                )}
                            </div>
                        </div>

                        <button
                            className="dashboard-company__sidebar-close"
                            onClick={toggleSidebar}
                            aria-label="Fechar menu"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    <div className="dashboard-company__sidebar-quick-info">
                        <div
                            className="dashboard-company__sidebar-info-item dashboard-company__sidebar-info-item--clickable"
                            onClick={() => {
                                setShowWalletModal(true);
                                setIsSidebarOpen(false);
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && setShowWalletModal(true)}
                        >
                            <Wallet size={16} />
                            <span className="dashboard-company__sidebar-info-label">Saldo</span>
                            <span className="dashboard-company__sidebar-info-value">
                                R$ {((wallet?.balance ?? 0) - (wallet?.reserved ?? 0)).toFixed(2)}
                            </span>
                        </div>
                    </div>

                    <nav className="dashboard-company__sidebar-nav">
                        {mobileMenuItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = activeTab === item.id;
                            return (
                                <button
                                    key={item.id}
                                    className={`dashboard-company__sidebar-nav-item ${isActive ? 'dashboard-company__sidebar-nav-item--active' : ''}`}
                                    onClick={() => handleTabClick(item.id)}
                                >
                                    <Icon size={20} />
                                    <span className="dashboard-company__sidebar-nav-label">{item.label}</span>
                                </button>
                            );
                        })}

                        <button
                            className="dashboard-company__sidebar-nav-item"
                            onClick={() => {
                                setShowProfileModal(true);
                                setIsSidebarOpen(false);
                            }}
                        >
                            <UserCog size={20} />
                            <span className="dashboard-company__sidebar-nav-label">Configurações</span>
                        </button>

                        <button
                            className="dashboard-company__sidebar-nav-item"
                            onClick={() => {
                                setShowPremiumModal(true);
                                setIsSidebarOpen(false);
                            }}
                        >
                            <Crown size={20} />
                            <span className="dashboard-company__sidebar-nav-label">Premium</span>
                        </button>

                        <button
                            className="dashboard-company__sidebar-nav-item"
                            onClick={() => {
                                setShowFavoritesModal(true);
                                setIsSidebarOpen(false);
                            }}
                        >
                            <Heart size={20} />
                            <span className="dashboard-company__sidebar-nav-label">Favoritos</span>
                        </button>

                        <button
                            className="dashboard-company__sidebar-nav-item"
                            onClick={() => {
                                setShowCreateJobModal(true);
                                setIsSidebarOpen(false);
                            }}
                        >
                            <PlusCircle size={20} />
                            <span className="dashboard-company__sidebar-nav-label">Nova Vaga</span>
                        </button>
                    </nav>
                </aside>

                {isSidebarOpen && (
                    <div className="dashboard-company__sidebar-overlay" onClick={toggleSidebar} />
                )}
            </div>

            <main className="dashboard-company__content">
                {renderContent()}
            </main>

            {/* Modais */}
            {showProfileModal && (
                <ProfileModal
                    open={showProfileModal}
                    user={user}
                    atualizarPerfil={handleUpdateProfile}
                    onClose={() => setShowProfileModal(false)}
                />
            )}

            {showCreateJobModal && (
                <CreateJobModal
                    open={showCreateJobModal}
                    onClose={() => setShowCreateJobModal(false)}
                    criarVaga={handleCreateJob}
                    isFreePlan={isFreePlan}
                    companyAddress={companyAddress}
                    onSaveCompanyAddress={handleSaveCompanyAddress}
                    walletBalance={(wallet?.balance ?? 0) - (wallet?.reserved ?? 0)}  // ⬅️ NOVO
                    onOpenWallet={() => setShowWalletModal(true)}                     // ⬅️ NOVO
                    toast={showJobToast}
                />
            )}

            {showFavoritesModal && (
                <div
                    className="dashboard-company__favorites-overlay"
                    onClick={() => setShowFavoritesModal(false)}
                >
                    <div
                        className="dashboard-company__favorites-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="dashboard-company__favorites-close"
                            onClick={() => setShowFavoritesModal(false)}
                            aria-label="Fechar"
                        >
                            ×
                        </button>
                        <Favorites />
                    </div>
                </div>
            )}

            <WalletModal
                isOpen={showWalletModal}
                onClose={() => setShowWalletModal(false)}
                userData={user}
            />

            <GoldSubscriptionCompany
                isOpen={showPremiumModal}
                onClose={() => setShowPremiumModal(false)}
                onSubscribe={handlePremiumSubscribe}
            />

            {/* Modal de Trial Gold integrando logo após o tutorial */}
            {showGoldTrialModal && (
                <CompanyGoldTrialModal onClose={() => setShowGoldTrialModal(false)} />
            )}

            <ChatSupportButton userId={user?.id} userType={user?.userType} />

            <Toast
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ message: "", type: "success" })}
            />

            {showTutorial && (
                <TutorialCompany
                    hasSeenTutorial={user.hasSeenTutorial}
                    onComplete={(dontShowAgain) => {
                        if (dontShowAgain) {
                            markTutorialSeen(user.id);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default DashboardCompany;